<script>
import { mapActions, mapState } from "vuex";
import i18n from "@/plugins/i18n";

import SU2Questions from "./Su2Questions.vue";

export default {
  name: "My Surveys",

  components: {
    SU2Questions
  },

  data() {
    return {
      localeName: "surveys.revised_surveys",

      stepIndex: 5,
      stepValidation: null,

      surveys: [],
      deletedSurveys: [],
      activeTab: "created",

      loadingSurveys: false,

      processTrack: [],

      createSurvey: false,
      createSurveyName: null,
      createSurveyDescription: null,

      su2QuestionsActive: false,
      createdSurvey: null,

      // sorter //

      base: "",
      ascending: true,
    };
  },

  computed: {
    ...mapState('settings', [
			'process_track_id',
			'steps'
		]),

    activeTabSurveys() {
      if (this.activeTab == 'created') {
        return this.surveys;
      } else if (this.activeTab == 'deleted') {
        return this.deletedSurveys;
      } else {
        return [];
      }
    },

    disableActions() {
      return this.stepValidation != null;
    },

    surveyNameExists() {
      if (this.createSurvey) {
        return this.surveys.some(item => item.su2_survey_name == this.createSurveyName);
      } else {
        return false;
      }
    },

    surveyTableHeaders() {
      return [
        {val: "name", text: i18n.t(`${this.localeName}.main_table.name`)},
        {val: "created_on", text: i18n.t(`${this.localeName}.main_table.created_on`)},
        this.activeTab === 'created' ? {
          val: "end_on", 
          text: i18n.t(`${this.localeName}.main_table.end_on`)
        } : this.activeTab === 'deleted' ? {
          val: "deleted_on", 
          text: i18n.t(`${this.localeName}.main_table.deleted_on`)
        } : null,
        {val: "survey_status", text: i18n.t(`${this.localeName}.main_table.survey_status`)}
      ];
    },

    surveyExistsInYear() {
      if (this.$user.role_id === 1) return false;

      const currentYear = new Date().getFullYear();
      return this.surveys.some(x => new Date(x.created_on).getFullYear() === currentYear && x.is_send) 
        || this.deletedSurveys.some(x => new Date(x.created_on).getFullYear() === currentYear && x.is_send)
    },
  },

  async mounted() {
    await this.getSurveys();
    await this.handleSetStepIncomplete();
  },

  methods: {
    ...mapActions("settings", [
      "updateProcessTrack"
    ]),

    async handleSetStepIncomplete(){
      if (!this.steps || this.steps.length === 0) {
				setTimeout(async () => {
					await this.handleSetStepIncomplete();
				}, 150);
				return;
			}

      const incompleteStepIndex = this.steps.findIndex((item, index) => (item.completed == false && item.message != undefined && index < this.stepIndex));
      const activeSurveys = this.surveys.filter(item => item.is_deleted == false);

      if (activeSurveys.length == 0) {
        this.stepValidation = null;
      } else {
        activeSurveys.sort((a,b) => b.su2_survey_id - a.su2_survey_id);
        const lastSurvey = activeSurveys[0];
        if (incompleteStepIndex > -1) {
          if (lastSurvey.is_completed == true) {
            this.stepValidation = null;
            await this._updateProcessTrack(false);
          } else {
            this.stepValidation = this.steps[incompleteStepIndex];
          }
        } else {
          this.stepValidation = null;
        }
      }
    },

    async _updateProcessTrack(show_message = true) {
      const params = {
        is_step_5_completed: true,
        updated_by: this.$user.user_id,
        reporting_process_track_id: this.process_track_id
      }
      await this.updateProcessTrack(params)
        .then( async (res) => {
          if (show_message)
            this.$notify('success', res.data.messageName);

          this.stepValidation = null;
        })
        .catch(({ response }) => {
          if (show_message)
            this.$notify("error", response.data.messageName);
        })
    },

    async handleClickStepError() {
      this.$router.push(this.stepValidation.route_link);
    },

    ...mapActions("surveys", [
      "getSU2Surveys",
      "su2createSurvey"
    ]),

    async getSurveys() {
      await this.setLoadingSurveys(true);
      const params = {
        created_by: this.$user.user_id,
        company_id: this.$user.company_id,
        is_deleted: false,
        is_approved: true,
      };
      await this.getSU2Surveys(params)
        .then((res) => {
          this.surveys = res.data;
        })
        .catch(() => {
          console.log("No active surveys.");
        })
        .finally(async () => {
          await this.getMySurveysDeleted();
          await this.setLoadingSurveys(false);
        });
    },
    
    async getMySurveysDeleted() {
      await this.setLoadingSurveys(true);
      const params = {
        created_by: this.$user.user_id,
        company_id: this.$user.company_id,
        is_deleted: true,
        is_approved: true,
      };
      this.getSU2Surveys(params)
        .then((res) => {
          this.deletedSurveys = res.data;
        })
        .catch(() => {
          console.log("No deleted surveys.");
        })
        .finally(async () => {
          await this.setLoadingSurveys(false);
        });
    },

    async createSurveySU2() {
      if (this.disableActions || this.surveyNameExists || this.surveyExistsInYear) return;

      const params = {
        company_id: this.$user.company_id,
        created_by: this.$user.user_id,
        su2_survey_name: this.createSurveyName,
        su2_s_description: this.createSurveyName
      };
      await this.su2createSurvey(params)
        .then(async (res) => {
          await this.getSurveys();
          this.$toast.open({
            message: this.$parseResMessage(res.data.messageName),
            type: "success",
            duration: 5000,
            dismissible: true,
          });
          this.$router.push({path: "/materiality-analysis-survey-questions", query: {s: res.data.su2_survey_id, t: res.data.survey_token}});
        })
        .catch(({ response }) => {
          this.$toast.open({
            message: this.$parseResMessage(response.data.messageName),
            type: "error",
            duration: 5000,
            dismissible: true,
          });
        })
    },

    handleCreateSurvey(bool) {
      if ((this.surveyExistsInYear && bool) || this.disableActions) return;
      this.createSurvey = bool;
      
      if (bool == true) {
        this.createSurveyName = `Materiality Analysis Survey - ${localStorage.getItem("company_name")}`;
      }
    },

    handleSu2Ok() {
      this.handleCreateSurvey(false);
      this.su2QuestionsActive = true;
    },

    handleResetAll() {
      this.createSurvey = false;
      this.su2QuestionsActive = false;
    },

    goToSurveyDetails(survey) {
      if (this.disableActions) return;
      this.$router.push({ path: "/materiality-analysis-survey-details", query: {s: survey.su2_survey_id} });
    },

    sortAlphabetically(_input, _base) {
      if(_base != this.base) {
        this.ascending = true;
      } else {
        this.ascending = !this.ascending;
      }
      
      this.participants = this.$sortAlphabetically(_input, _base, this.ascending);
      this.base = _base;
    },

    sortByCompleted(_input, _base) {
      _input.forEach( item => {
        if (item.is_sent && item.is_resolved) {
          item["status"] = 1;
        } else if (item.is_sent && !item.is_resolved) {
          item["status"] = 2;
        } else if (!item.is_sent && !item.is_resolved) {
          item["status"] = 3;
        }
      });

      if(_base != this.base) {
        this.ascending = true;
      } else {
        this.ascending = !this.ascending;
      }

      this.participants = this.$sortNumerically(_input, _base, this.ascending);
      this.base = _base; 
    },

    sortByBoolean(_input, _base) {
      _input.forEach( item => {
        if (item[_base] === true) {
          item["boolean"] = 1;
        } else {
          item["boolean"] = 0;
        }
      });

      if(_base != this.base) {
        this.ascending = true;
      } else {
        this.ascending = !this.ascending;
      }

      this.participants = this.$sortNumerically(_input, "boolean", this.ascending);
      this.base = _base; 
    },

    // handlers

    async handleClickTab(tab) {
      if (this.disableActions) return;

      if (tab != this.activeTab) {
        await this.setActiveTab(tab);
      }
    },

    // setters //

    async setLoadingSurveys(bool) {
      this.loadingSurveys = bool;
    },

    async setActiveTab(tab) {
      this.activeTab = tab;
    }
  },
};
</script>

<template>
  <div class="surveys-page flex-all-centered-col">
    <div class="survey-tabs flex-all-centered-row" :class="{'disabled-by-step-validation': disableActions}" v-if="!su2QuestionsActive">
      <div class="tab flex-all-centered-row" :class="{'selected-tab': activeTab == 'created'}" @click="handleClickTab('created')">
        {{ $t(`${localeName}.buttons.created_surveys`) }}
      </div>
      <div class="tab flex-all-centered-row" :class="{'selected-tab': activeTab == 'deleted'}" @click="handleClickTab('deleted')">
        {{ $t(`${localeName}.buttons.deleted_surveys`) }}
      </div>
    </div>

    <div class="tab-container flex-all-centered-col" v-if="!su2QuestionsActive">
      <div class="validation-error flex-all-centered-row" v-if="stepValidation" @click.self="handleClickStepError()">
        <div class="flex-all-centered-row text">
          <i class="fa-solid fa-circle-exclamation"></i>
          {{ stepValidation.message }}
        </div>
        <button @click="_updateProcessTrack()">
          {{ $t(`${localeName}.buttons.confirm`) }}
        </button>
      </div>
      
      <div class="validation-error flex-all-centered-row" v-if="surveyExistsInYear">
        <div class="flex-all-centered-row text">
          <i class="fa-solid fa-circle-exclamation"></i>
          {{ $t(`${localeName}.misc.quota_full`) }}
        </div>
      </div>

      <div class="surveys-table flex-all-centered-col">
        <div class="headers flex-all-centered-row">
          <div class="header" v-for="header in surveyTableHeaders" :key="header.val">
            {{ header.text }}
          </div>
        </div>

        <div class="survey nothing-yet no-clicking flex-all-centered-row" v-if="activeTabSurveys.length == 0 && createSurvey == false">
          {{ $t('noData.text2') }}
        </div>

        <div class="survey flex-all-centered-row" :class="{'no-clicking': activeTab == 'deleted', 'disabled-by-step-validation': disableActions}"
          v-for="(survey, index) in activeTabSurveys" :key="index"
          @click="goToSurveyDetails(survey)" @keydown.enter="goToSurveyDetails(survey)"
          tabindex="0"
        >
          <div class="survey-cell">
            {{ survey.su2_survey_name }}
          </div>
          <div class="survey-cell">
            {{ survey.created_on | dateFormat }}
          </div>
          <div class="survey-cell" v-if="activeTab === 'created'">
            {{ survey.end_date | dateFormat }}
          </div>
          <div class="survey-cell" v-if="activeTab === 'deleted'">
            {{ survey.deleted_on | dateFormat }}
          </div>
          <div class="survey-cell status-cell">
            <div class="status-text">
              {{ 
                !survey.is_send ? $t(`${localeName}.misc.not_sent`) 
                : (!survey.is_completed ? $t(`${localeName}.misc.incomplete`) 
                : $t(`${localeName}.misc.complete`)) 
              }}
            </div>

            <div class="select-survey-button" v-if="activeTab == 'created'">
              <i class="fa-solid fa-square-arrow-up-right"
                v-b-tooltip.hover.bottom="{title: $t(`${localeName}.misc.show_detailed`)}"
              >
              </i>
            </div>
          </div>
        </div>

        <div class="create-survey flex-all-centered-row" v-if="activeTab == 'created' && createSurvey == true">
          <div class="left-aligned-cell">
            <input type="text" v-model="createSurveyName">
          </div>
          <div class="left-aligned-cell">
            <button name="cancel" @click="handleCreateSurvey(false)">
              {{ $t(`${localeName}.buttons.cancel`) }}
            </button>
            <button name="confirm" @click="createSurveySU2()" :disabled="surveyNameExists">
              {{ $t(`${localeName}.buttons.create`) }}
            </button>
          </div>
          <div class="left-aligned-cell name-warning" v-if="surveyNameExists">
            <i class="fa-solid fa-circle-exclamation"></i>
            {{ $t(`${localeName}.misc.survey_exists`) }}
          </div>
        </div>
      </div>
      <div class="middle-section flex-all-centered-row" v-if="activeTab == 'created' && createSurvey == false">
        <button @click="handleCreateSurvey(true)" :class="{'disabled-by-step-validation': surveyExistsInYear || loadingSurveys || disableActions}" :disabled="surveyExistsInYear || loadingSurveys">
          {{ $t(`${localeName}.buttons.create`) }}
        </button>
      </div>
    </div>

    <SU2Questions v-if="su2QuestionsActive" :su2SurveyId="createdSurvey.su2_survey_id" :su2SurveyToken="createdSurvey.survey_token" @questionsDone="handleResetAll()"/>
  </div>
</template>

<style lang="scss" scoped>
  .surveys-page {
    justify-content: flex-start;
    padding: 1em;
    transition: all 150ms;
  }

  .survey-tabs {
    height: 30px;
    border-radius: 100px;
    border: thin solid var(--sustainfinity-navy);
    overflow: hidden;

    > .tab {
      height: 100%;
      width: 100%;
      text-align: center;
      background-color: whitesmoke;
      color: var(--sustainfinity-navy);

      transition: all 150ms;
      cursor: pointer;
      &:hover {
        background-color: rgb(226, 226, 226);
      }
    }

    > .selected-tab {
      background-color: var(--sustainfinity-navy) !important;
      color: whitesmoke !important;
      pointer-events: none !important;
    }
  }

  .middle-section {
    justify-content: flex-start;
    
    > button {
      border-radius: .3em;
      background-color: var(--sustainfinity-green);
      color: whitesmoke;
      border: 0;
      box-shadow: 0 0 .5em .1em gray;
      padding: .2em .5em .2em .5em;
      margin-top: 1em;

      &:hover {
        background-color: #4bbd00;
      }

      &:focus {
        outline: thin solid black !important;
      }

      &:active {
        scale: .95;
      }
    }
  }

  .validation-error {
    justify-content: space-between;
    background-color: var(--invalid-input);
    color: whitesmoke;
    font-weight: bold;
    padding: .3em .5em;
    border-radius: .3em;
    margin-top: 1em;

    transition: all 250ms;
    cursor: pointer;
    /* &:hover {
      background-color: rgb(162, 68, 31);
    } */

    > .text {
      justify-content: flex-start;
      > i {
        margin-right: .5em;
      }
    }

    > button {
      padding: .3em .5em;
      color: whitesmoke;
      background-color: transparent;
      border: thin solid whitesmoke;
      border-radius: .3em;

      transition: all 250ms;
      &:hover {
        background-color: #c14d1f;
      }
    }
  }

  .surveys-table {
    margin-top: 1em;
    justify-content: flex-start;
    border-radius: 1em;
    overflow: hidden;
    box-shadow: 0 .25em .5em .1em gray;

    > .headers {
      justify-content: flex-start;
      text-align: center;

      > .header {
        padding: .5em .5em .5em .5em;

        width: 100%;
        background-color: var(--sustainfinity-navy);
        color: whitesmoke;
        border-right: thin solid whitesmoke;
        
        &:last-of-type {
          border-right: 0 !important;
        }
      }
    }

    > .survey {
      justify-content: flex-start;
      text-align: center;
      background-color: whitesmoke;
      border-bottom: thin solid lightgray;
      
      transition: all 150ms;
      cursor: pointer;

      &:hover {
        background-color: lightgray;
      }

      &:focus {
        background-color: lightgray;
        border: 0;
        outline: 0;
      }

      > .survey-cell {
        padding: .5em .5em .5em .5em;
        width: 100%;  
      }

      > .status-cell {
        position: relative;
        
        > .select-survey-button {
          position: absolute;
          right: 1em;
          top: 50%;
          transform: translateY(-50%);

          > i {
            color: var(--sustainfinity-green);

            transition: all 150ms;
            cursor: pointer;

            &:hover {
              color: var(--sustainfinity-navy);
            }
          }
        }
      }
    }

    > .nothing-yet {
      padding: 1em;
      justify-content: center;
      font-size: 1.3em;
      font-weight: bold;
    }
  }

  .create-survey {
    justify-content: flex-start;
    text-align: center;
    background-color: whitesmoke;
    border-bottom: thin solid lightgray;

    > .left-aligned-cell {
      padding: 1em;
      width: 25%;
      display: flex;
      justify-content: flex-start;
      align-items: center;

      > input {
        border-radius: .5em;
        border: 0;
        outline: thin solid lightgray;
        background: 0;
        padding: .1em .25em;
        width: 100%;

        &:focus {
          outline: thin solid darkgray !important;
        }
      }

      > button {
        border-radius: 100px;
        border: 0;
        outline: 0;
        font-size: .9em;
        color: white;
        padding: .25em .75em;
        min-width: 90px;
        width: fit-content;
        margin-left: 1em;

        transition: all 200ms;
        &:focus {
          outline: thin solid black;
        }

        &:active {
          scale: .95;
        }

        &:disabled {
          background-color: darkgray !important;
          pointer-events: none !important;
        }
      }

      > button[name="cancel"] {
        background-color: var(--invalid-input);
        &:hover {
          background-color: #c14d1f;
        }
      }

      > button[name="confirm"] {
        background-color: var(--sustainfinity-green);
        &:hover {
          background-color: #388b00;
        }
      }
    }

    > .name-warning {
      font-weight: bold;
      color: var(--invalid-input);
      > i {
        transform: translateY(5%);
        padding-right: .25em;
      }
    }
  }

  .no-clicking {
    pointer-events: none !important;
  }

  .disabled-by-step-validation {
    pointer-events: none !important;
    filter: grayscale(100%);
  }
</style>